import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Bot, User as UserIcon, Building2, ClipboardCheck, Landmark, Wrench, Accessibility, Globe2, BookOpen, Brain, Mail, ListChecks, RefreshCw, Star, Quote, MapPin, ShieldCheck, UserCheck, Hotel, Server } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import api from "@/api/client";
import { PERMIT_CATALOG } from "@/data/permitCatalog";

const SERVICES = [
  { n: "01", id: "permit-expediting", title: "Permit Expediting & Strategy", icon: Building2, body: "Proactive AHJ navigation and schedule protection for complex commercial projects nationwide.",
    bullets: ["Multi-jurisdiction permit management", "Review comment resolution", "Online approvals & project management", "Deferred submittal coordination", "Certificate of Occupancy sequencing", "New construction permitting"] },
  { n: "02", id: "due-diligence", title: "Due Diligence & Preliminary Research", icon: ClipboardCheck, body: "Pre-acquisition and pre-design code risk assessments that identify problems before they become expensive surprises.",
    bullets: ["Due diligence / preliminary research", "Zoning compliance analysis", "Land use permits", "Change of use review", "IBC / IFC code gap analysis", "TI trigger assessment"] },
  { n: "03", id: "entitlements", title: "Entitlements & Planning Approvals", icon: Landmark, body: "Navigate entitlement processes, planning approvals, and zoning with an experienced advocate on your side.",
    bullets: ["Entitlements & planning approval", "Zoning variance", "Conditional use permits (CUP)", "Site design review", "DRC presentation support", "Green building approval"] },
  { n: "04", id: "building-mep", title: "Building, MEP & Trades", icon: Wrench, body: "Full trade coordination across all disciplines for ground-up and tenant improvement projects of any scale.",
    bullets: ["Building, Mechanical, Electrical & Plumbing", "Fire approval & fire suppression", "Drafting services", "Bureau of Engineering coordination", "Sanitation approval", "Cal/OSHA compliance"] },
  { n: "05", id: "accessibility", title: "Accessibility & Specialized Permits", icon: Accessibility, body: "ADA, disabled access, coastal development, and specialized permit categories requiring expert navigation.",
    bullets: ["ADA / Disabled access compliance", "Coastal development permit", "Health department approval", "Sign permits", "Liquor license coordination", "LA County special events permit"] },
  { n: "06", id: "national-brand", title: "National Brand Programs & CRA", icon: Globe2, body: "Dedicated permitting program management for national retailers, QSRs, and commercial developers with multi-state rollouts.",
    bullets: ["CRA coordination", "Rollout program management", "Multi-state AHJ coordination", "Design manager support", "PM liaison & status reporting", "Fee & timeline projections"] },
];

const PROCESS = [
  { n: "01", title: "Project Assessment", body: "We review your scope, jurisdiction, timeline, and risk profile to build a permitting strategy before the first submission." },
  { n: "02", title: "AHJ Engagement", body: "Direct communication with building officials, fire marshals, and planning departments using technical authority — not just status checks." },
  { n: "03", title: "Active Management", body: "Proactive review comment resolution, deferred submittal coordination, and real-time updates to your project team." },
  { n: "04", title: "Project Closeout", body: "C/O sequencing, final inspection coordination, and closeout documentation so your project opens on time and on budget." },
];

const TESTIMONIALS = [
  { name: "Michael R.", role: "Project Manager — National Retail Developer",
    body: "Coast To Coast is the only permit consultant I've worked with who actually understands the plans. They caught a conflict between our fire suppression drawings and the architectural set before we submitted — saved us at minimum a 3-week delay and a full correction cycle. That kind of technical depth is rare." },
  { name: "Jennifer T.", role: "Design Manager — QSR National Brand",
    body: "We've done projects in 14 states and Coast To Coast is the only expediter we've found who can operate confidently in all of them. Their knowledge of Miami-Dade specifically is unmatched — that AHJ alone would have cost us months without them. They pay for themselves ten times over on any complex project." },
  { name: "Carlos M.", role: "Superintendent — Commercial General Contractor",
    body: "As a GC superintendent I've worked with a lot of expediters. Most of them just submit and wait. Coast To Coast actually communicates with the building department, knows the inspectors by name, and comes back with real answers. The difference on inspection scheduling alone is weeks on our schedule." },
  { name: "David K.", role: "VP Real Estate — Commercial Developer",
    body: "We engaged Coast To Coast for a due diligence review on a potential acquisition in California. Their report identified three code compliance issues that would have cost over $400K to remediate post-close. We renegotiated the purchase price accordingly. Best consulting investment we've made." },
  { name: "Sarah L.", role: "Tenant Rep — National Retail Leasing",
    body: "Our tenant improvement hit a wall on accessibility — the AHJ was requiring a full path of travel upgrade that our budget couldn't absorb. Coast To Coast came in, reviewed the scope against the 20% rule, and negotiated a compliant solution with the building department that worked for everyone. Saved the deal." },
  { name: "Robert A.", role: "Principal Architect — Commercial Architecture Firm",
    body: "I've referred Coast To Coast to three of my architecture clients in the past year. Every one of them came back to thank me. They speak the language of building officials because they've been on that side — and that changes everything when you're trying to resolve a third-cycle review comment at 4pm on a Friday." },
];

const COVERAGE = [
  "Los Angeles County", "Sacramento County", "San Diego", "Miami-Dade County", "Mecklenburg County",
  "Dallas / DFW", "Houston", "Austin", "New York Townships", "Philadelphia Metro", "Charlotte",
  "Atlanta", "Phoenix", "Las Vegas", "Denver", "Nashville", "Chicago Metro", "Seattle / King County",
  "Portland Metro", "Tampa / Hillsborough",
];

const MARQUEE_ITEMS = [
  "Permit Expediting", "AHJ Intelligence", "Code Compliance", "Due Diligence", "Accessibility Consulting",
  "Zoning & Entitlement", "Plan Review Support", "Deferred Submittals", "Certificate of Occupancy",
  "Coastal Development", "Green Building Approval", "Fire Approval",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 grain-overlay opacity-60 pointer-events-none" />
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-700">Nationwide Commercial Permit Consulting</span>
            </div>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.02]">
              From <em className="not-italic text-emerald-600">Coast</em> <br />
              To Coast
            </h1>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border-l-2 border-emerald-500 bg-white/80" data-testid="hero-catchphrase">
              <span className="text-sm font-mono italic text-slate-700">&ldquo;Experience from both sides of the counter.&rdquo;</span>
            </div>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed font-ibm-plex">
              Commercial permitting strategy, AHJ-specific intelligence, and code compliance consulting —
              backed by <strong className="text-slate-900">deep experience on both sides of the permit counter</strong>,
              in every major market across the country.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild data-testid="hero-cta-hire" size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-base">
                <Link to="/hire">Hire us as your expediter<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild data-testid="hero-cta-platform" size="lg" variant="outline" className="h-12 px-7 text-base border-slate-300 hover:bg-white">
                <Link to="/ahj-verify">Verify your AHJ</Link>
              </Button>
              <Button asChild data-testid="hero-cta-services" size="lg" variant="ghost" className="h-12 px-5 text-base text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50">
                <Link to="/services">Our services<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl" data-testid="landing-stats">
              {[
                { v: "3,221", l: "U.S. Counties" },
                { v: "500+", l: "Projects Delivered" },
                { v: "50", l: "States Served" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-slate-900 pl-4">
                  <div className="font-outfit text-3xl font-bold text-slate-900">{s.v}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="https://images.unsplash.com/photo-1646383642188-5cde1af30af7?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
                alt="Aerial of a U.S. metro"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-lg p-4 border border-white/40 shadow-lg">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">
                  <MapPin className="w-3 h-3" /> Both sides of the permit counter
                </div>
                <div className="text-sm font-medium text-slate-900">Plans Examiner · Dev Manager · Permit Strategist</div>
                <div className="text-xs text-slate-500 mt-1">IBC · IRC · IFC — across all 50 states</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-slate-200 shadow-lg rounded-lg p-4 w-56">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-600 mb-1">Insider perspective</div>
              <div className="font-outfit text-2xl font-bold text-slate-900">Fortune 500</div>
              <div className="text-xs text-slate-500 mt-1">national programs delivered</div>
            </div>
          </div>
        </div>
      </section>

      {/* AI + HUMAN EXPERTISE BAND — emphasizes Plans Examiner / AHJ-insider review */}
      <section className="bg-slate-900 text-white border-y border-slate-800" data-testid="landing-expert-band">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5" data-testid="expert-band-tag">
              <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400">AI Speed · Expert Reviewed · Human Insight</span>
            </div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              Not just AI. <br />
              <span className="text-emerald-400">Reviewed by a real Plans Examiner.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Every report we generate is backed by professionals who have <strong className="text-white">worked inside the AHJ</strong> — as
              consultants, code officials, and plan reviewers. We know what it takes
              to <strong className="text-white">go vertical</strong>. We&apos;ve sat at both sides of the counter, and we know what the
              AHJ is thinking before they say it.
            </p>
            <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-2xl">
              AI gives us speed and pattern recognition. Human experience gives us judgment,
              relationships, and the realistic call on what will and won&apos;t fly in your jurisdiction.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 self-center" data-testid="expert-band-pillars">
            {[
              { icon: UserCheck, title: "Plans Examiner Reviewed", body: "Real human plan review eyes on every project. Not just AI-generated guesses." },
              { icon: Landmark, title: "AHJ Insider Experience", body: "We've worked inside Building, Planning, Fire, and Public Works — at the counter and across it." },
              { icon: Building2, title: "Vertical Build Reality", body: "Field experience taking projects from intake → C/O. We know what construction actually requires." },
              { icon: Brain, title: "AI + Human Judgment", body: "Claude Haiku 4.5 for speed. Plans Examiner judgment for the call that matters." },
            ].map((p) => {
              const Ico = p.icon;
              return (
                <div key={p.title} className="bg-slate-800/60 border border-slate-700 rounded-xl p-5" data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mb-3">
                    <Ico className="w-4 h-4" />
                  </div>
                  <div className="font-outfit font-bold text-white text-base leading-tight mb-1.5">{p.title}</div>
                  <div className="text-xs text-slate-300 leading-relaxed">{p.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-slate-200 bg-white overflow-hidden">
        <div className="flex gap-8 py-5 whitespace-nowrap animate-marquee text-sm font-mono uppercase tracking-[0.18em] text-slate-500">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((m, i) => (
            <span key={i} className="flex items-center gap-8">
              {m}<Star className="w-3 h-3 text-emerald-500 fill-emerald-500" />
            </span>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">About</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Both Sides of <br />the Permit Counter
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Most consultants know one side of the process. We know all of it — from the plan examiner&apos;s desk
              to the developer&apos;s boardroom, in markets from Los Angeles to Miami and everywhere in between.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              <strong className="text-slate-900">Coast To Coast Permits</strong> was founded by a commercial
              permitting strategist with over 20 years spanning government plan review, field inspection,
              property development management, and nationwide permit expediting for Fortune 500 brands.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Having served as a <strong>Plans Examiner for multiple municipalities</strong>, the team brings
              a rare insider perspective — understanding exactly how AHJs think, what triggers review comments,
              and how to resolve them before they become schedule problems.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: "Plans Examiner", sub: "IBC · IRC · IFC — Multiple municipalities" },
              { title: "Development Manager", sub: "Permit Consulting — National rollouts" },
              { title: "End-to-End Project Management", sub: "Permit Coordination — Fortune 500 commercial programs nationwide" },
              { title: "Full Lifecycle Expertise", sub: "Both sides of the permit counter — government & private sector" },
            ].map((c) => (
              <div key={c.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-lg transition-all">
                <div className="font-outfit text-lg font-bold text-slate-900 mb-2">{c.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-white border-y border-slate-200" data-testid="landing-services">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="mb-14 max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Services</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Full Lifecycle <br /><span className="text-slate-500">Expertise</span>
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              From site acquisition through certificate of occupancy — ground up and tenant improvements
              across all trades, in every major market in the country.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="group bg-[#F8FAFC] border border-slate-200 rounded-2xl p-7 hover:bg-white hover:border-emerald-400 hover:shadow-xl transition-all" data-testid={`service-${s.n}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center group-hover:bg-emerald-600 transition">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="font-outfit text-2xl font-bold text-slate-300 group-hover:text-emerald-600 transition">{s.n}</span>
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2 leading-tight">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.body}</p>
                  <ul className="space-y-1.5 text-xs text-slate-500">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2"><span className="text-emerald-600">→</span>{b}</li>
                    ))}
                  </ul>
                  <Link to={`/qa#${s.id}`} data-testid={`service-qa-${s.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-emerald-700 hover:text-emerald-900">
                    Common questions →
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPECIALIZED VERTICALS — Hotels + Data Centers */}
      <section id="verticals" className="bg-slate-50 border-b border-slate-200" data-testid="landing-verticals">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="mb-12 max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Specialized verticals</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Deep expertise where<br /><span className="text-slate-500">it matters most.</span>
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Two verticals where we do more than expedite — we run the program end-to-end.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="landing-verticals-grid">
            {/* HOTELS */}
            <Link to="/hotels" data-testid="vertical-card-hotels" className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-emerald-300 hover:shadow-xl transition-all">
              <div className="relative h-52 bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-800 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=70" alt="Hotel ground-up construction" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-400">Vertical · Hospitality</div>
                    <div className="font-outfit text-2xl font-bold text-white mt-1">Hotel Permitting</div>
                  </div>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                    <Hotel className="w-4 h-4" />
                  </span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 leading-relaxed">
                  End-to-end hotel development — <strong className="text-slate-900">from land sourcing to Certificate of Occupancy to business license</strong>. A 13-phase roadmap covering entitlements, civil, building, MEP, fire/life safety, elevators, cranes, deferred submittals, TCO/CO, and operational licenses.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {["Zoning & land use", "Building & MEP", "Fire/life safety", "Elevators & cranes", "13-phase roadmap"].map((t) => (
                    <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{t}</span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 group-hover:text-emerald-900">
                  Explore hotel services <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>

            {/* DATA CENTERS */}
            <Link to="/data-center" data-testid="vertical-card-data-centers" className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-indigo-300 hover:shadow-xl transition-all">
              <div className="relative h-52 bg-gradient-to-br from-indigo-900 via-slate-900 to-slate-800 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=70" alt="Data center facility" className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-indigo-300">Vertical · Mission-critical</div>
                    <div className="font-outfit text-2xl font-bold text-white mt-1">Data Center Permitting</div>
                  </div>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/40 text-indigo-300">
                    <Server className="w-4 h-4" />
                  </span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Zoning, planning, building and MEP for mission-critical facilities. We manage utility interconnection, on-site generation, cooling review, environmental permits, and multi-track approvals — from entitlement through final CO.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {["Zoning & DRC", "Utility interconnection", "Electrical & cooling", "Environmental", "Multi-track approvals"].map((t) => (
                    <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">{t}</span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-indigo-700 group-hover:text-indigo-900">
                  Explore data center services <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* PERMIT TYPES SHOWCASE */}
      <section id="permit-types" className="bg-white border-b border-slate-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="mb-12 max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Permit catalog</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Commercial Permits<br /><span className="text-slate-500">We Handle</span>
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              From ground-up new construction to specialty coastal and accessibility approvals — Coast To Coast
              expedites every commercial permit type your project needs, in every U.S. jurisdiction.
            </p>
          </div>
          <PermitTypeShowcase />
        </div>
      </section>

      {/* AHJ INTEL PLATFORM */}
      <section id="platform" className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">AHJ Intel Platform</div>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            The Intelligence Behind <br /><span className="text-slate-500">Coast To Coast</span>
          </h2>
          <p className="mt-6 text-base text-slate-600 leading-relaxed">
            Jurisdiction-specific permitting knowledge — available on demand. Real answers.
            Not just the code. How it actually works.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: BookOpen, title: "AHJ-Specific Knowledge Base", body: "Deep jurisdiction guides — portal quirks, real review timelines, insider process notes, key contacts, and what the city website doesn't tell you." },
            { icon: Brain, title: "AI-Powered Q&A", body: "Ask any permitting or code question. The AI responds using Coast To Coast's documented knowledge — not generic training data. Jurisdiction-specific, technically authoritative answers." },
            { icon: Mail, title: "Direct Expert Access", body: "Premium subscribers submit questions directly. Our expert responds personally within 24 hours. Complex questions answered by a real permitting professional." },
            { icon: ListChecks, title: "Submittal Checklists", body: "AHJ-specific submittal requirements, common review comment libraries, and deferred submittal guides built from real project experience." },
            { icon: Accessibility, title: "Accessibility Intelligence", body: "State-by-state accessibility requirements beyond federal ADA — including local amendments, TI trigger thresholds, and path of travel scoping by jurisdiction." },
            { icon: RefreshCw, title: "Living Knowledge Base", body: "Continuously updated as AHJs change their processes, adopt new code editions, or shift portals. Your questions today help build better answers for everyone tomorrow." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-slate-900 hover:shadow-xl transition-all">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2 leading-tight">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* AI LIVE EXAMPLE */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 mb-5">
                <Sparkles className="w-3 h-3 text-indigo-600" />
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-indigo-700">AHJ Intel — Live Example</span>
              </div>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                Real answers. <br /><span className="text-slate-500">Not generic code.</span>
              </h2>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                Subscribers ask jurisdiction-specific questions and get jurisdiction-specific answers —
                informed by deep plan review, expediting, and Fortune 500 program work.
              </p>
              <Button asChild data-testid="landing-cta-chat" className="mt-7 bg-slate-900 hover:bg-slate-800 text-white h-11">
                <Link to="/chat">Try the assistant<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>

            <div className="lg:col-span-8">
              <div data-testid="ai-live-preview" className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl shadow-slate-900/5">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50/70">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <div className="ml-3 flex items-center gap-2 text-xs font-mono text-slate-500">
                    <Bot className="w-3 h-3 text-indigo-600" />ctcpermits.com/chat
                  </div>
                  <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                    Live preview
                  </div>
                </div>

                <div className="px-6 md:px-8 py-6 md:py-7 bg-[#FAFBFD] space-y-5">
                  <div className="flex gap-3 justify-end">
                    <div className="max-w-[82%] bg-slate-900 text-white text-sm leading-relaxed rounded-2xl rounded-br-sm px-4 py-3">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-1.5">Subscriber</div>
                      What&apos;s the real plan review timeline for a commercial TI permit in Mecklenburg County
                      and what triggers a third review cycle?
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center flex-none mt-0.5">
                      <UserIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center flex-none mt-0.5">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="max-w-[90%] bg-white border border-slate-200 text-slate-800 text-sm leading-relaxed rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">AHJ Intel — Mecklenburg County, NC</div>
                      <p className="mb-2 font-semibold text-slate-900">Commercial TI Plan Review</p>
                      <p className="text-[13.5px]">
                        Published timeline is 15 business days. <strong>Actual first-cycle reality is 18–25 business days</strong>{" "}
                        for most commercial TIs, longer during high-volume periods (typically Q1).
                      </p>
                      <p className="mt-3 font-semibold text-slate-900">Most common third-cycle triggers:</p>
                      <ul className="space-y-1.5 mt-1.5 text-[13.5px]">
                        <li>— Incomplete deferred submittal schedule on cover sheet</li>
                        <li>— Fire sprinkler hydraulic calculations misaligned with architectural</li>
                        <li>— Accessibility path of travel not addressed when scope exceeds 50% of building value</li>
                        <li>— MEP conflicts not coordinated before submission</li>
                      </ul>
                      <p className="mt-3 text-[13.5px]">
                        <strong>Portal note:</strong> Mecklenburg uses the OneStop portal. Pre-application meetings are
                        strongly recommended for projects over 10,000 SF — they significantly reduce second-cycle comments.
                      </p>
                      <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 italic">
                        Verify current timelines directly with the AHJ for active projects.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/50 text-xs text-slate-500">
                  <span className="font-mono uppercase tracking-[0.15em]">Tagged: Mecklenburg County · Commercial TI</span>
                  <span className="font-mono">Stored to community KB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTC PROCESS */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">How we work</div>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            The CTC Process
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((p) => (
            <div key={p.n} className="relative bg-white border border-slate-200 rounded-2xl p-7 hover:border-emerald-400 hover:shadow-lg transition-all">
              <div className="font-outfit text-5xl font-bold text-emerald-600 mb-4 leading-none">{p.n}</div>
              <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-900 text-white" data-testid="landing-testimonials">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="mb-14 max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3">Client Experience</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              Trusted by National Brands
            </h2>
            <p className="mt-6 text-base text-slate-300 leading-relaxed">
              What GCs, project managers, and tenants say about working with Coast To Coast Permits.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-slate-800/60 border border-slate-700 rounded-2xl p-7 hover:bg-slate-800 hover:border-emerald-500 transition-all">
                <div className="flex items-center gap-1 mb-4">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />)}
                </div>
                <Quote className="w-6 h-6 text-emerald-400 mb-3 opacity-60" />
                <p className="text-sm text-slate-200 leading-relaxed mb-5">{t.body}</p>
                <div className="border-t border-slate-700 pt-4">
                  <div className="font-outfit font-bold text-white text-sm">{t.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Coverage</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Nationwide <br /><span className="text-slate-500">AHJ Knowledge</span>
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Deep operational knowledge of the most complex permitting jurisdictions in the country — not just
              the code, but the people, processes, and portals that actually move projects forward.
              <strong className="text-slate-900"> All 50 states. Coast to coast.</strong>
            </p>
            <Button asChild data-testid="coverage-cta-map" className="mt-7 bg-emerald-600 hover:bg-emerald-500 text-white h-11">
              <Link to="/map">Open the interactive map<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-4">Key Metro Markets</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {COVERAGE.map((c) => (
                <div key={c} className="bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 hover:border-emerald-400 hover:bg-emerald-50/40 transition">
                  <MapPin className="w-3 h-3 inline mr-2 text-emerald-600" />{c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-emerald-600 text-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h3 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight">
              Your project deserves an expert,<br />not a portal.
            </h3>
            <p className="text-emerald-50 mt-3 max-w-xl">
              Direct access to coast-to-coast permitting expertise —
              no offshore teams, no layers, no runaround.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild data-testid="cta-hire-btn" size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-7">
              <Link to="/hire">Schedule a consultation<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild data-testid="cta-services-btn" size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-7">
              <Link to="/services">Explore our services</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10 grid grid-cols-1 md:grid-cols-4 gap-6 text-sm text-slate-500">
          <div>
            <img
              src="/brand/ctc-logo-240.png"
              alt="Coast To Coast Permits"
              data-testid="footer-logo-img"
              className="h-24 w-auto object-contain mb-3"
              width="76"
              height="96"
            />
            <div className="font-outfit text-lg font-bold text-slate-900">Coast To Coast Permits</div>
            <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-700">Nationwide Commercial Permitting</div>
            <div className="mt-2 text-xs">Headquartered in DFW, Texas — Serving All 50 States</div>
            <a href="mailto:Admin@Ctcpermits.com" className="block mt-1 text-xs text-emerald-700 hover:underline">Admin@Ctcpermits.com</a>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-slate-700 mb-2">Services</div>
            <a href="#services" className="block text-xs hover:text-slate-900">Permit Expediting</a>
            <a href="#services" className="block text-xs hover:text-slate-900">Due Diligence</a>
            <a href="#services" className="block text-xs hover:text-slate-900">Entitlements</a>
            <a href="#services" className="block text-xs hover:text-slate-900">National Brand Programs</a>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-slate-700 mb-2">Platform</div>
            <Link to="/services" className="block text-xs hover:text-slate-900">Services</Link>
            <Link to="/ahj-verify" className="block text-xs hover:text-slate-900">AHJ Verify</Link>
            <Link to="/map" className="block text-xs hover:text-slate-900">AHJ Map</Link>
            <Link to="/chat" className="block text-xs hover:text-slate-900">AI Assistant</Link>
            <Link to="/hire" className="block text-xs hover:text-slate-900">Hire as Expediter</Link>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-slate-700 mb-2">Coverage</div>
            <div className="text-xs">3,221 U.S. counties</div>
            <div className="text-xs">500+ projects delivered</div>
            <div className="text-xs">All 50 states</div>
          </div>
        </div>
        <div className="border-t border-slate-100 max-w-screen-2xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between text-xs text-slate-400 gap-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span>© 2026 Coast To Coast Permits. Commercial Permit &amp; Code Consulting.</span>
            <span className="hidden md:inline text-emerald-600">·</span>
            <em className="not-italic text-emerald-700 font-medium" data-testid="footer-catchphrase">&ldquo;Experience from both sides of the counter.&rdquo;</em>
          </div>
          <div className="font-mono uppercase tracking-[0.2em]">v1.0 · ctcpermits.com</div>
        </div>
      </footer>
    </div>
  );
}

function PermitTypeShowcase() {
  // Catalog is bundled client-side — eliminates the "loading" flash entirely
  const catalog = PERMIT_CATALOG;

  const grouped = {};
  for (const t of catalog.types || []) {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  }
  const order = ["building", "mep", "fire_safety", "planning", "accessibility", "specialty", "environmental", "closeout"];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {order.filter((c) => grouped[c]).map((cat) => (
        <div key={cat} className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">
            {catalog.categories[cat] || cat}
          </div>
          <div className="flex flex-wrap gap-2">
            {grouped[cat].map((t) => (
              <span key={t.id}
                className="text-xs px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-700 hover:border-emerald-400 hover:text-slate-900 transition cursor-default"
                title={t.description}
                data-testid={`landing-permit-${t.id}`}>
                {t.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

